/**
 * The person's conversation on their page, while they type and the host replies
 * (specs/formal/living-thread).
 *
 * The page draws what the server last said plus its own messages still on their way. A message
 * the person sends carries the page's own id (client_id), the same on every try, so the server
 * keeps it once and a read of the conversation can tell it is the same message. Reads can come
 * back out of order: one older than the read already drawn is dropped. A reply holds its target
 * by the target's client_id when it is the person's own message, so it survives a read turning
 * that message into the server's, and it goes only once the target has its server id.
 */
import type { Attachment, WatchMessage } from "../../../shared/types";

/** What a reply points at: a server message, or the person's own message by its page id. */
export type ReplyTarget = { id: string } | { cid: string };

/** A message the person sent from this page, kept until a read shows it. */
export interface Outgoing {
  cid: string;
  webinar_id: string;
  body: string;
  attachments?: Attachment[];
  reply?: ReplyTarget;
  /** The host question this message answers (the reply is that question). */
  answer_to?: string;
  /** The announcement this answers ("Reply to Maya"): the thread quotes it. */
  reply_post?: { id: string; body: string };
  state: "waiting" | "sending" | "sent" | "failed";
  /** The server's answer, once it came back. */
  sent?: WatchMessage;
}

export interface ThreadState {
  /** The number of the read the page last drew from. */
  seq: number;
  server: WatchMessage[];
  outgoing: Outgoing[];
}

/** One bubble the page draws: a stored message, or one of the person's still on its way. */
export interface Drawn {
  message: WatchMessage;
  /** Set while it is the page's own and not yet seen in a read. */
  outgoing?: Outgoing;
}

export const emptyThread: ThreadState = { seq: 0, server: [], outgoing: [] };

/** A new page id for a send: letters and digits, long enough never to meet another. */
export function newClientId(random: () => number = Math.random): string {
  let s = "c";
  while (s.length < 20) s += Math.floor(random() * 36).toString(36);
  return s;
}

/**
 * A read of the conversation came back. One older than the read already drawn is dropped; the
 * person's own messages stay until a read shows them by their client_id.
 */
export function applyRead(state: ThreadState, seq: number, messages: WatchMessage[]): ThreadState {
  if (seq < state.seq) return state;
  const seen = new Set(messages.map((m) => m.client_id).filter(Boolean));
  return { seq, server: messages, outgoing: state.outgoing.filter((o) => !seen.has(o.cid)) };
}

/** The person pressed Send. */
export function addOutgoing(state: ThreadState, o: Omit<Outgoing, "state">): ThreadState {
  return { ...state, outgoing: [...state.outgoing, { ...o, state: "waiting" }] };
}

function setOutgoing(state: ThreadState, cid: string, patch: Partial<Outgoing>): ThreadState {
  if (!state.outgoing.some((o) => o.cid === cid)) return state;
  return { ...state, outgoing: state.outgoing.map((o) => (o.cid === cid ? { ...o, ...patch } : o)) };
}

export const markSending = (s: ThreadState, cid: string) => setOutgoing(s, cid, { state: "sending" });
export const markFailed = (s: ThreadState, cid: string) => setOutgoing(s, cid, { state: "failed" });
/** "Not sent. Tap to try again": the same message, with the same page id, waits to go again. */
export const retryOutgoing = (s: ThreadState, cid: string) => setOutgoing(s, cid, { state: "waiting" });

/** The server's answer to a send. An answer for a message a read already showed changes nothing. */
export function markSent(state: ThreadState, cid: string, sent: WatchMessage): ThreadState {
  return setOutgoing(state, cid, { state: "sent", sent });
}

/** The server id a reply target has now, or null while the target is still on its way. */
function resolveTarget(state: ThreadState, target: ReplyTarget): string | null {
  if ("id" in target) return target.id;
  const stored = state.server.find((m) => m.client_id === target.cid);
  if (stored) return stored.id;
  return state.outgoing.find((o) => o.cid === target.cid)?.sent?.id ?? null;
}

/**
 * The next message to send: one at a time, in the order they were pressed, skipping a reply
 * whose target has no server id yet (it goes once it has one).
 */
export function nextSend(state: ThreadState): { outgoing: Outgoing; reply_to: string | null } | null {
  if (state.outgoing.some((o) => o.state === "sending")) return null;
  for (const o of state.outgoing) {
    if (o.state !== "waiting") continue;
    const reply_to = o.reply ? resolveTarget(state, o.reply) : null;
    if (o.reply && !reply_to) continue;
    return { outgoing: o, reply_to };
  }
  return null;
}

/** Every bubble in order: the stored messages, then the person's still on their way. */
export function drawThread(state: ThreadState, now: number): Drawn[] {
  const stored = state.server.toSorted((a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id));
  const mine = state.outgoing.map((o): Drawn => {
    const reply_to = o.reply ? resolveTarget(state, o.reply) : null;
    return {
      outgoing: o,
      message: o.sent ?? {
        id: o.cid,
        sender: "registrant",
        body: o.body || null,
        created_at: now,
        client_id: o.cid,
        ...(o.attachments?.length ? { attachments: o.attachments } : {}),
        ...(reply_to ? { reply_to } : {}),
        ...(o.answer_to ? { answer_to: o.answer_to } : {}),
        ...(o.reply_post ? { reply_post: o.reply_post } : {}),
      },
    };
  });
  return [...stored.map((message) => ({ message })), ...mine];
}

/** The key a bubble keeps across a read turning it from the page's own into the server's. */
export function bubbleKey(d: Drawn): string {
  return d.outgoing?.cid ?? d.message.client_id ?? d.message.id;
}
